import { isEqual, sortBy } from 'lodash';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';

const secretPlaceholder = '●●●●●●●●●●●●●●●●●●';

export enum GrantType {
  Implicit = 'implicit',
  Hybrid = 'hybrid',
  AuthorizationCode = 'authorization_code',
  ClientCredentials = 'client_credentials',
  ResourceOwnerPassword = 'password',
  DeviceFlow = 'urn:ietf:params:oauth:grant-type:device_code',
}

export enum ProtocolType {
  OIDC = 'oidc',
  SAML = 'saml',
}

export const GrantTypes = {
  Implicit: [GrantType.Implicit],
  ImplicitAndClientCredentials: [GrantType.Implicit, GrantType.ClientCredentials],
  Code: [GrantType.AuthorizationCode],
  CodeAndClientCredentials: [GrantType.AuthorizationCode, GrantType.ClientCredentials],
  Hybrid: [GrantType.Hybrid],
  HybridAndClientCredentials: [GrantType.Hybrid, GrantType.ClientCredentials],
  ClientCredentials: [GrantType.ClientCredentials],
  ResourceOwnerPassword: [GrantType.ResourceOwnerPassword],
  ResourceOwnerPasswordAndClientCredentials: [GrantType.ResourceOwnerPassword, GrantType.ClientCredentials],
  DeviceFlow: [GrantType.DeviceFlow],
};

export type GrantTypesKey = keyof typeof GrantTypes;

export const getKeyFromGrantTypes = (items: GrantType[]) => {
  for (const [key, values] of Object.entries(GrantTypes)) {
    if (isEqual(sortBy(values), sortBy(items))) return key as GrantTypesKey;
  }
};

export const getGrantTypesFromKey = (key: GrantTypesKey) => {
  return GrantTypes[key];
};

export enum StandardScopes {
  OpenId = 'openid',
  Profile = 'profile',
  Email = 'email',
  Address = 'address',
  Phone = 'phone',
  OfflineAccess = 'offline_access',
}

export type ClientSecret = {
  id?: number;
  value?: string;
  description: string;
};

export enum AccessTokenType {
  Jwt = 0,
  Reference = 1,
}

export enum TokenUsage {
  ReUse = 0,
  OneTimeOnly = 1,
}

export const defaultClientIds = [
  'academy',
  'admin',
  'exportimportclient',
  'sptapp',
  'sptappoffline',
  'sptapptest',
  'partners',
  'patclient',
  'portal',
  'projectsclient',
  'sharing',
  'sptApiClient',
  'structmasterapp',
  'structmasterappoffline',
  'structmasterapptest',
];

type BaseClientItem = {
  clientId: string;
  clientName: string;
  requirePkce: boolean;
  requireClientSecret: boolean;
  allowAccessTokensViaBrowser: boolean;
  allowOfflineAccess: boolean;
  alwaysIncludeUserClaimsInIdToken: boolean;
  allowedGrantTypes: GrantType[];
  grantTypesKey?: GrantTypesKey;
  requireConsent: boolean;
  redirectUris: string[];
  postLogoutRedirectUris: string[];
  allowedScopes: (StandardScopes | string)[];
  accessTokenLifetime?: number;
  refreshTokenUsage?: TokenUsage;
  accessTokenType?: AccessTokenType;
  protocolType: ProtocolType;

  enabled?: boolean;
  allowedCorsOrigins?: string[];
  identityTokenLifetime?: number;
  absoluteRefreshTokenLifetime?: number;
};

export type ClientItem = BaseClientItem & {
  clientSecrets: ClientSecret[];
};

export type ClientSummary = ClientItem;

export type EditClientItem = BaseClientItem & {
  secretsToAdd: ClientSecret[];
  secretsToEdit: Partial<ClientSecret>[];
  secretsToDelete: number[];
};

export type ClientUsageSummary = {
  userId: string;
  userName: string;
  email: string;
  organizationId: string;
  organizationName: string;
  createdOn: Date;
};

export const defaultClientItem: Partial<ClientItem> = {
  allowAccessTokensViaBrowser: false,
  allowOfflineAccess: false,
  alwaysIncludeUserClaimsInIdToken: false,
  requirePkce: false,
  requireClientSecret: false,
  requireConsent: false,
  redirectUris: [],
  postLogoutRedirectUris: [],
  clientSecrets: [],
  protocolType: ProtocolType.OIDC,
};

export const getGrantTypeLabel = (grant: GrantType) => {
  return (
    Object.entries(GrantType)
      .find((v) => v[1] === grant)
      ?.firstOrDefault() ?? grant
  );
};

export class ClientSummaryStore extends DataStore<ClientSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/clients`, []);
  }

  public async load(query: any) {
    return await this.handleCallAsync(async () => {
      if (!query.tableUpdate) {
        this._state.set((s) => {
          s.items = [];
          s.count = 0;
          return s;
        });
      }

      var response = await this.httpService.get<QueryResult<ClientSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);

      let items = response.data.items;
      for (const item of items) {
        item.grantTypesKey = getKeyFromGrantTypes(item.allowedGrantTypes);
        item.clientSecrets = item.clientSecrets.map((secret) => ({ ...secret, value: secretPlaceholder }));
        delete item.enabled;
        delete item.identityTokenLifetime;
        delete item.allowedCorsOrigins;
      }

      this._state.set((s) => {
        s.items = items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }
}

export class ClientItemStore extends FormStore<EditClientItem, ClientItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/clients`);
  }
}

export class ClientUsageSummaryStore extends DataStore<ClientUsageSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/clients`, []);
  }

  public async loadClientUsage(query: Partial<Query>, clientId: string) {
    return await this.handleCallAsync(async () => {
      if (!query.tableUpdate) {
        this._state.set((s) => {
          s.items = [];
          s.count = 0;
          return s;
        });
      }

      var response = await this.httpService.get<QueryResult<ClientUsageSummary>>(
        `${this.baseUrl}/${clientId}/tenants/?${DataStore.buildUrl(query as Query)}`
      );

      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }
}
